import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { PaginationMetadata, PositionsState } from "../types";
import { agentAxiosObj, axiosObj } from "../../config/apiClients";
import { BarNumber, ClosePosition, Portal, PositionStats } from "../../utils/types";

const adminPositionUrls = {
  open: "trade/open",
  buyAtPrice: "trade/pendingBuyPositions",
  takeProfit: "trade/pendingSellPositions",
  closed: "trade/close",
  stats: "trade/positionStats",
};

const agentPositionUrls = {
  open: "/positions/open",
  buyAtPrice: "/positions/buy-at-price",
  takeProfit: "/positions/take-profit",
  closed: "/positions/closed",
  stats: "/positions/stats",
};

const getPositionUrls = (portal: Portal) => (portal === "agent" ? agentPositionUrls : adminPositionUrls);

const getApiClient = (portal: Portal) => (portal === "agent" ? agentAxiosObj : axiosObj);

export type PositionFilters = {
  search?: string;
  tradeSymbol?: string;
  startDate?: string;
  endDate?: string;
};

export type PositionListQuery = PositionFilters & {
  portal: Portal;
  paramPage: number;
  paramLimit: number;
};

const toQueryParams = (filters: PositionFilters) => ({
  search: filters.search || undefined,
  tradeSymbol: filters.tradeSymbol === "all" ? undefined : filters.tradeSymbol,
  startDate: filters.startDate,
  endDate: filters.endDate,
});

const fetchPositionList = async (url: string, query: PositionListQuery) => {
  const params = { paramPage: query.paramPage, paramLimit: query.paramLimit, ...toQueryParams(query) };
  const response = await getApiClient(query.portal).get(url, { params });
  return response.data;
};

const readErrorMessage = (error: unknown, fallback: string) =>
  (error as { message?: string })?.message || fallback;

const initialPagination: PaginationMetadata = {
  totalItems: 0,
  currentPage: 1,
  totalPages: 1,
  hasNextPage: false,
};

const readPagination = (payload: any): PaginationMetadata => ({
  totalItems: payload?.totalItems ?? 0,
  currentPage: payload?.page ?? 1,
  totalPages: payload?.totalPages ?? 1,
  hasNextPage: payload?.hasNextPage ?? false,
});

const initialState: PositionsState = {
  listLoading: { open: false, closed: false, pendingBuy: false, pendingSell: false },
  listPagination: {
    open: { ...initialPagination },
    closed: { ...initialPagination },
    pendingBuy: { ...initialPagination },
    pendingSell: { ...initialPagination },
  },
  isMutating: false,
  openPositions: [],
  closedPositions: [],
  pendingBuyPositions: [],
  pendingSellPositions: [],
  metalSummary: [],
  positionStats: null,
  positionStatsLoading: false,
  positionStatsError: null,
};

export const getOpenedPositions = createAsyncThunk(
  "positions/open",
  async (query: PositionListQuery, { rejectWithValue }) => {
    try {
      return await fetchPositionList(getPositionUrls(query.portal).open, query);
    } catch (error) {
      return rejectWithValue(readErrorMessage(error, "Failed to Fetch Open Positions"));
    }
  }
);

export const getPendingBuyPositions = createAsyncThunk(
  "positions/buyAtPrice",
  async (query: PositionListQuery, { rejectWithValue }) => {
    try {
      return await fetchPositionList(getPositionUrls(query.portal).buyAtPrice, query);
    } catch (error) {
      return rejectWithValue(readErrorMessage(error, "Failed to Fetch Pending Buy Positions"));
    }
  }
);

export const getPendingSellPositions = createAsyncThunk(
  "positions/takeProfit",
  async (query: PositionListQuery, { rejectWithValue }) => {
    try {
      return await fetchPositionList(getPositionUrls(query.portal).takeProfit, query);
    } catch (error) {
      return rejectWithValue(readErrorMessage(error, "Failed to Fetch Pending Sell Positions"));
    }
  }
);

export const getClosedPositions = createAsyncThunk(
  "positions/closed",
  async (query: PositionListQuery, { rejectWithValue }) => {
    try {
      return await fetchPositionList(getPositionUrls(query.portal).closed, query);
    } catch (error) {
      return rejectWithValue(readErrorMessage(error, "Failed to Fetch Closed Positions"));
    }
  }
);

// Optional customer search, metal (XAUUSD / XAGUSD) and plain days ("2026-09-01");
// without them the stats cover everyone, both metals, all time
export const getPositionStats = createAsyncThunk<
  PositionStats,
  PositionFilters & { portal: Portal },
  { rejectValue: string }
>(
  "positions/stats",
  async ({ portal, ...filters }, { rejectWithValue }) => {
    try {
      const response = await getApiClient(portal).get(getPositionUrls(portal).stats, { params: toQueryParams(filters) });
      return response.data.payload;
    } catch (error) {
      return rejectWithValue(readErrorMessage(error, "Failed to Fetch Position Stats"));
    }
  }
);

export const manualClosePosition = createAsyncThunk(
  "positions/manualClose",
  async (closePosition: ClosePosition, { rejectWithValue }) => {
    try {
      const response = await axiosObj.patch('trade/manualClosePosition', closePosition);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Close Position' });
    }
  }
);

export const assignBarNumber = createAsyncThunk(
  "positions/assignBarNumber",
  async (data: BarNumber, { rejectWithValue }) => {
    try {
      const response = await axiosObj.patch('trade/barNumber', data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Assign Bar Number' });
    }
  }
);

const positionsSlice = createSlice({
  name: "positions",
  initialState,
  reducers: {
    updateOpenTrade: (state, action) => {
      const positionIndex = state.openPositions.findIndex(item => item.dealId === action.payload.dealId);
      if (positionIndex !== -1) {
        const currentPosition = state.openPositions[positionIndex];
        const updatedUnfilledMetal = currentPosition.unfilledMetal - action.payload.amount;
        state.openPositions[positionIndex] = {
          ...state.openPositions[positionIndex],
          unfilledMetal: updatedUnfilledMetal
        }
      }
    },
    updateBarNumber: (state, action) => {
      const positionIndex = state.openPositions.findIndex(item => item._id === action.payload.tradeId);
      if (positionIndex !== -1) {
        state.openPositions[positionIndex] = {
          ...state.openPositions[positionIndex],
          barNumber: action.payload.barNumber
        }
      }
    }
  },
  extraReducers(builder) {
    builder
      .addCase(getOpenedPositions.pending, (state) => {
        state.listLoading.open = true;
      })
      .addCase(getOpenedPositions.fulfilled, (state, action) => {
        state.listLoading.open = false;
        state.openPositions = action.payload.payload.allBuyTrades;
        state.metalSummary = action.payload.payload.metalSummary;
        state.listPagination.open = readPagination(action.payload.payload);
      })
      .addCase(getOpenedPositions.rejected, (state) => {
        state.listLoading.open = false;
        state.openPositions = [];
        state.metalSummary = [];
      })
      .addCase(getClosedPositions.pending, (state) => {
        state.listLoading.closed = true;
      })
      .addCase(getClosedPositions.fulfilled, (state, action) => {
        state.listLoading.closed = false;
        state.closedPositions = action.payload.payload.sellTrades;
        state.metalSummary = action.payload.payload.metalSummary;
        state.listPagination.closed = readPagination(action.payload.payload);
      })
      .addCase(getClosedPositions.rejected, (state) => {
        state.listLoading.closed = false;
        state.closedPositions = [];
        state.metalSummary = [];
      })
      .addCase(getPendingBuyPositions.pending, (state) => {
        state.listLoading.pendingBuy = true;
      })
      .addCase(getPendingBuyPositions.fulfilled, (state, action) => {
        state.listLoading.pendingBuy = false;
        state.pendingBuyPositions = action.payload.payload.pendingBuyTrades;
        state.metalSummary = action.payload.payload.metalSummary;
        state.listPagination.pendingBuy = readPagination(action.payload.payload);
      })
      .addCase(getPendingBuyPositions.rejected, (state) => {
        state.listLoading.pendingBuy = false;
        state.pendingBuyPositions = [];
        state.metalSummary = [];
      })
      .addCase(getPendingSellPositions.pending, (state) => {
        state.listLoading.pendingSell = true;
      })
      .addCase(getPendingSellPositions.fulfilled, (state, action) => {
        state.listLoading.pendingSell = false;
        state.pendingSellPositions = action.payload.payload.pendingSellTrades;
        state.metalSummary = action.payload.payload.metalSummary;
        state.listPagination.pendingSell = readPagination(action.payload.payload);
      })
      .addCase(getPendingSellPositions.rejected, (state) => {
        state.listLoading.pendingSell = false;
        state.pendingSellPositions = [];
        state.metalSummary = [];
      })
      .addCase(getPositionStats.pending, (state) => {
        state.positionStatsLoading = true;
        state.positionStatsError = null;
      })
      .addCase(getPositionStats.fulfilled, (state, action) => {
        state.positionStatsLoading = false;
        state.positionStats = action.payload;
      })
      .addCase(getPositionStats.rejected, (state, action) => {
        state.positionStatsLoading = false;
        state.positionStatsError = action.payload ?? 'Failed to Fetch Position Stats';
      })
      .addCase(manualClosePosition.pending, (state) => {
        state.isMutating = true;
      })
      .addCase(manualClosePosition.fulfilled, (state) => {
        state.isMutating = false;
      })
      .addCase(manualClosePosition.rejected, (state) => {
        state.isMutating = false;
      })
      .addCase(assignBarNumber.pending, (state) => {
        state.isMutating = true;
      })
      .addCase(assignBarNumber.fulfilled, (state) => {
        state.isMutating = false;
      })
      .addCase(assignBarNumber.rejected, (state) => {
        state.isMutating = false;
      });
  }
})

export const { updateOpenTrade, updateBarNumber } = positionsSlice.actions;

export default positionsSlice.reducer;
